import { useMemo } from "react";
import { nb, resolveJourney } from "../../lib/journey";
import { joinStatusFromGroup, sendersFromMessageLog, normalizeNumberToken, normalizeRoleToken, joinedNumbersFromEvents, awaitingJoinFromEvents } from "../../lib/members";
import { filterComplaints } from "../../lib/complaints";
// import { useGroupComplaints, useGroupMessageLog, useStaffToneFlags, useMemberChanges, useGroupEvents, useSentimentHistory } from "../../lib/api";
import { useTenantTerms } from "../../lib/TenantConfigContext";
import { useGroupComplaints, useGroupMessageLog, useStaffToneFlags, useMemberChanges, useGroupEvents, useSentimentHistory, useFeedback } from "../../lib/api";
import { latestFeedbackForGroup, parseRecommendationScore } from "../../lib/feedback";

// Advisor + Customer come straight off the group row; the internal roles come
// from GET /api/stakeholders/{location}. Join status is always re-derived from
// THIS group's members_* columns, and anyone who has spoken in the group
// counts as joined whatever members_* says.
// Same token the API uses to identify a built-in member that was removed.
export function memberToken(m) {
  return `${normalizeNumberToken(m.number)}|${normalizeRoleToken(m.key)}`;
}
// `awaitingJoin` (awaitingJoinFromEvents): numbers added from the desk that
// have not joined since; they show Not Joined whatever older history says.
export function buildMembers(g, stakeholders, senderNumbers, changes, awaitingJoin) {
  const list = [
    { role: "Advisor", name: g["Service Advisor Name"], number: g["Service Advisor Number"], key: "Service Advisor" },
    { role: "Customer", name: g["Customer Name"], number: g["Customer Number"], key: "Customer" },
    ...(nb(g["Alternate Customer Number"])
      ? [{ role: "Alt Customer", name: `${g["Customer Name"] || "Customer"} (Alt)`, number: g["Alternate Customer Number"], key: "Alt Customer" }]
      : []),
    ...(stakeholders || []).map((s) => ({ role: s.role, name: s.name, number: s.number, key: s.role })),
  ];
  const removed = new Set(changes?.removed || []);
  const base = list
    .filter((m) => nb(m.name) || nb(m.number))
    .filter((m) => !removed.has(memberToken(m)))
    .map((m) => {
      const status = awaitingJoin?.has(normalizeNumberToken(m.number))
        ? "Not Joined"
        : joinStatusFromGroup(g, m.key, m.name, m.number, senderNumbers);
      return { ...m, status, joined: status === "Joined" };
    });
  const baseNumbers = new Set(base.map(m => normalizeNumberToken(m.number)));

  // People added from the desk were never sent an invite, so they show
  // "Not Joined" until they speak in the group.
  const extras = (changes?.added || [])
    .filter(a => !baseNumbers.has(normalizeNumberToken(a.number)))
    .map((a) => {
      const token = normalizeNumberToken(a.number);
      const spoke = !awaitingJoin?.has(token) && Boolean(senderNumbers && senderNumbers.has(token));
      const status = spoke ? "Joined" : "Not Joined";
      return { role: a.role || "Added member", name: a.name, number: a.number, key: "Added", status, joined: spoke };
    });
  return [...base, ...extras];
}

// One read of everything the desk summarises about a group — the chat
// header chips and the Details section headers show the same numbers.
export function useDeskSummary(group, row, stakeholders) {
  const { stageOverlay, featureFlags } = useTenantTerms();
  const { data: messageLog } = useGroupMessageLog(row);
  const { data: complaintsData } = useGroupComplaints(row);
  const { data: toneData } = useStaffToneFlags(row);
  const { data: memberChanges } = useMemberChanges(row);
  const { data: eventsData } = useGroupEvents(row);
  const { data: feedbackData } = useFeedback();

  return useMemo(() => {
    if (!group) return null;
    const latestFeedback = latestFeedbackForGroup(feedbackData?.rows || [], group);
    const recommendScore = latestFeedback ? parseRecommendationScore(latestFeedback["Recommendation Score (1-10)"]) : null;

    const feedbackIsGood = recommendScore != null && recommendScore >= 7;
    // console.log(feedbackIsGood);
    // console.log("fjhbvksdgvkusgkdv", feedbackData);
    const steps = resolveJourney(group, stageOverlay, featureFlags).map((s) =>
      s.key === "post_delivery_feedback" && feedbackIsGood
        ? { ...s, done: true, state: "completed" }
        : s
    );
    const current = steps.find((s) => s.state === "current") || null;
    const done = steps.filter((s) => s.state === "completed").length;

    const members = buildMembers(
      group,
      stakeholders,
      new Set([...sendersFromMessageLog(messageLog?.rows), ...joinedNumbersFromEvents(eventsData?.rows)]),
      memberChanges,
      awaitingJoinFromEvents(eventsData?.rows, messageLog?.rows)
    );
    const pendingMembers = members.filter((m) => m.status === "Not Joined").length;

    const complaints = complaintsData?.rows || [];
    const openComplaints = filterComplaints(complaints, "open");
    const complaintStage = openComplaints[0]?.stage || null;

    const flags = (toneData?.rows || []).filter((f) => f.risk_level !== "low");
    const conductOpen = flags.filter((f) => f.status === "open").length;

    return {
      steps,
      current,
      done,
      members,
      pendingMembers,
      complaintsEnabled: complaintsData?.config ? complaintsData.config.enabled !== false || complaints.length > 0 : true,
      openComplaints: openComplaints.length,
      complaintStage,
      sentiment: String(group["Sentiment"] || "").trim(),
      conductOpen,
    };
  }, [group, stakeholders, stageOverlay, featureFlags, messageLog, complaintsData, toneData, memberChanges, eventsData, feedbackData]);
}
